import type {
  CheckLink,
  Comment,
  Context,
  Remark,
  User,
  Workflow,
} from '@app/application/communication/types'
import { RemarkContextTypes } from '@app/application/communication/types'
import { orderBy } from 'lodash'
import { UNTAGGED_FILTER } from './constants'

export const ALL_CATEGORY = 'all'
export const FILTERED_CATEGORY = 'filtered'
export const UNREAD_CATEGORY = 'unread'
export const PULSE_OVERVIEW_CATEGORY = 'pulse_overview'
export const CLIENT_MEETING_CATEGORY = 'ClientMeeting'
export const COMPANY_CATEGORY = 'Company'
export const CLIENT_OVERVIEW_SECTION = 'Client overview'

export function groupCommunicationsByContext(
  values: (Remark | CheckLink)[],
  rootLedgerId: number | undefined,
  contextGetter: (
    ledgerId: number,
    contextType: string,
    contextId: number
  ) => Context | null,
  workflows: Workflow[] | null,
  enforceSingleHome = false
): Record<string, Record<string, (Remark | CheckLink)[]>> {
  const result: Record<string, Record<string, (Remark | CheckLink)[]>> = {}
  const safeWorkflows = workflows ?? []
  for (const value of values) {
    const context = contextGetter(
      rootLedgerId || value.ledgerId,
      value.contextType,
      value.contextId
    )
    if (!context) {
      continue
    }
    const bucket =
      value.kind === 'remark-card' ? (value as Remark).bucket : null
    // Meeting-tied talking points (action/discussed) span periods and are
    // surfaced in the inbox regardless of the requested ledger. One from another
    // period belongs under Talking points only - not in the (period-scoped)
    // workflow/context section of the period currently being viewed.
    // The `open` bucket is different: it is a talking-point-tagged remark with no
    // client meeting, i.e. an outstanding to-do that merely carries the tag. It
    // has a genuine workflow/context home and must keep it, otherwise a tagged
    // open to-do vanishes from the open-to-do filter whenever the viewed period
    // differs from its own (it would only show under Talking points).
    const crossLedgerTalkingPoint =
      bucket !== null &&
      bucket !== 'open' &&
      rootLedgerId !== undefined &&
      value.ledgerId !== rootLedgerId
    const paths = crossLedgerTalkingPoint
      ? []
      : pathsForContext(context, safeWorkflows, enforceSingleHome)
    if (bucket) {
      paths.push({ category: CLIENT_MEETING_CATEGORY, item: bucket })
    }
    for (const path of paths) {
      if (!result[path.category]) {
        result[path.category] = {}
      }
      if (!result[path.category][path.item]) {
        result[path.category][path.item] = []
      }
      result[path.category][path.item].push(value)
    }
  }
  return result
}

function pathsForContext(
  context: Context,
  workflows: Workflow[],
  enforceSingleHome = false
): { category: string; item: string }[] {
  const result = []
  if (
    [
      RemarkContextTypes.ACCOUNT,
      RemarkContextTypes.RECONCILIATION_TEXT,
    ].includes(context.type)
  ) {
    const owningWorkflows = workflows.filter((workflow) =>
      context.type === RemarkContextTypes.ACCOUNT
        ? workflow.accountIds.includes(context.id)
        : workflow.reconciliationTextIds.includes(context.id)
    )
    // The cross-workflow company inbox lists every workflow at once, so a
    // remark on an account/recon-text shared by several workflows would render
    // under each of them. Collapse it to a single canonical home there: the
    // default workflow when it owns the context, otherwise the first owning
    // workflow. The workflow-scoped view passes enforceSingleHome=false so the
    // remark keeps showing under whichever workflow is being viewed.
    const homeWorkflows =
      enforceSingleHome && owningWorkflows.length > 0
        ? [
            owningWorkflows.find((workflow) => workflow.default) ??
              owningWorkflows[0],
          ]
        : owningWorkflows
    for (const workflow of homeWorkflows) {
      result.push({
        category: workflow.name,
        item: context.name,
      })
    }
    if (result.length === 0) {
      // Fall back to the default workflow's section. `workflows` is loaded
      // asynchronously, so it can be momentarily empty (or missing the default)
      // while `groupedItems` recomputes; guard against it to avoid crashing the
      // whole inbox render. The item is regrouped once workflows finish loading.
      const defaultWorkflow = workflows.find((w) => w.default)
      if (defaultWorkflow) {
        result.push({
          category: defaultWorkflow.name,
          item: context.name,
        })
      }
    }
  } else if (
    context.type === RemarkContextTypes.COMPANY ||
    context.type === RemarkContextTypes.COMPANY_WORKFLOW_STATUS
  ) {
    result.push({ category: COMPANY_CATEGORY, item: CLIENT_OVERVIEW_SECTION })
  } else if (context.type === RemarkContextTypes.CLIENT_MEETING) {
    // Meeting-owned remarks are routed solely by their talking-point bucket
    // (see groupCommunicationsByContext), so no base path is added here.
  } else {
    result.push({ category: context.type, item: context.name })
  }
  return result
}

export function combineNotesAndTodos(
  notes: Record<string, Record<string, (Remark | CheckLink)[]>>,
  todos: Record<string, Record<string, (Remark | CheckLink)[]>>
): Record<string, Record<string, (Remark | CheckLink)[]>> {
  const result: Record<string, Record<string, (Remark | CheckLink)[]>> = {}
  const categories = Object.keys(notes).concat(Object.keys(todos))
  for (const category of categories) {
    result[category] = {}
    const items = Object.keys(notes[category] || {}).concat(
      Object.keys(todos[category] || {})
    )
    for (const item of items) {
      const notesInItem = (notes[category] || {})[item] || []
      const todosInItem = (todos[category] || {})[item] || []
      result[category][item] = orderBy(
        notesInItem.concat(todosInItem),
        ['sortTimestamp'],
        ['desc']
      )
    }
  }
  return result
}

export type TalkingPointBucket = 'open' | 'action' | 'discussed'

// Canonical display order for the talking-point buckets.
export const TALKING_POINT_BUCKETS: TalkingPointBucket[] = [
  'open',
  'action',
  'discussed',
]

export interface DatedRemarkGroup {
  // null for CheckLinks or remarks lacking a meeting date (e.g. the open bucket)
  completedAt: string | null
  items: (Remark | CheckLink)[]
}

// Groups talking points by their client meeting's completion date, newest
// meeting first, with each group sorted by sortTimestamp desc. Items without a
// meeting date (CheckLinks, open points) collapse into a single trailing group.
export function groupByMeetingDate(
  items: (Remark | CheckLink)[]
): DatedRemarkGroup[] {
  const byDate = new Map<string | null, (Remark | CheckLink)[]>()
  for (const item of items) {
    const completedAt =
      item.kind === 'remark-card'
        ? (item as Remark).clientMeetingCompletedAt ?? null
        : null
    if (!byDate.has(completedAt)) {
      byDate.set(completedAt, [])
    }
    byDate.get(completedAt)!.push(item)
  }

  return Array.from(byDate.entries())
    .sort(([a], [b]) => {
      if (a === null) return 1
      if (b === null) return -1
      return b.localeCompare(a)
    })
    .map(([completedAt, groupItems]) => ({
      completedAt,
      items: orderBy(groupItems, ['sortTimestamp'], ['desc']),
    }))
}

export function filterRemarks(
  remarks: Remark[],
  searchTerm: string,
  noteType: string,
  tags: (number | string)[],
  userIds: (number | 'external')[],
  commentById: (id: number) => Comment,
  userById: (id: number) => User
): Remark[] {
  const termsToSearch = []
  if (searchTerm.length > 0) {
    termsToSearch.push(searchTerm)
  }
  if (noteType === 'mentions') {
    termsToSearch.push(`@${window.Silverfin?.currentUserUsername()}`)
  }
  return filterRemarksByUsers(
    filterRemarksByTags(
      filterCommunicationsByStrings(remarks, commentById, termsToSearch),
      tags
    ),
    commentById,
    userById,
    userIds
  )
}

export function filterCommunicationsByStrings<T extends Remark | CheckLink>(
  communications: T[],
  commentById: (id: number) => Comment,
  terms: string[]
): T[] {
  if (terms.length === 0) {
    return communications
  }
  return communications.filter((communication) =>
    terms.every(
      (term) =>
        communication.text.toLowerCase().indexOf(term) !== -1 ||
        communication.commentIds.some(
          (commentId) =>
            commentById(commentId).text.toLowerCase().indexOf(term) !== -1
        )
    )
  )
}

function filterRemarksByTags(
  remarks: Remark[],
  tags: (number | string)[]
): Remark[] {
  if (tags.length === 0) {
    return remarks
  } else if (tags.includes(UNTAGGED_FILTER)) {
    return remarks.filter((remark) => remark.tagIds.length === 0)
  } else {
    return remarks.filter((remark) =>
      remark.tagIds.some((tagId) => tags.includes(tagId))
    )
  }
}

function filterRemarksByUsers(
  remarks: Remark[],
  commentById: (id: number) => Comment,
  userById: (id: number) => User,
  userIds: (number | 'external')[]
): Remark[] {
  if (userIds.length === 0) {
    return remarks
  }
  return remarks.filter(
    (remark) =>
      userIds.some((userId) =>
        userId === 'external'
          ? userById(remark.authorId).external
          : userId === remark.authorId
      ) ||
      remark.commentIds.some((commentId) => {
        const authorId = commentById(commentId).authorId
        return userIds.some((userId) =>
          userId === 'external'
            ? userById(authorId).external
            : userId === authorId
        )
      })
  )
}
